import { toggleHideEdge } from 'components/Graph/GraphApp/GraphEdge';
import { toggleHideNode } from 'components/Graph/GraphApp/GraphNode';
import type { GraphRendererPlugin } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import PixiCull from 'pixi-cull';
import type { CompositeHiding } from 'utils/pixi/compositeHiding';
import { uncull } from 'utils/pixi/uncull';

// pixi-cull adds some extra attributes with customizable names to renderable objects.
// We prefix these with "cull" to prevent conflicts with other libraries and functions.
const CULL_DIRTY_PROPERTY = 'cullDirty';
const CULL_SPATIAL_PROPERTY = 'cullSpatial';

const CULL_VISIBLE_PROPERTY = Symbol('Cull-visible');

const cullConfig = {
  dirty: CULL_DIRTY_PROPERTY,
  spatial: CULL_SPATIAL_PROPERTY,
  visible: CULL_VISIBLE_PROPERTY,
};

function installCullHideProperty(
  displayObject: CompositeHiding<any>,
  toggleHide: (hide: boolean) => void,
) {
  Object.defineProperty(displayObject, CULL_VISIBLE_PROPERTY, {
    get() {
      return !this.reasonsToHide.has('CULLED');
    },
    set(value: boolean) {
      toggleHide(!value);
    },
  });
}

export const cull: GraphRendererPlugin = (pluginInterface) => {
  const pixiCull = new PixiCull.SpatialHash(cullConfig);

  const performCull = () =>
    pixiCull.cull(pluginInterface.viewport.getVisibleBounds());

  pixiCull.addContainer(pluginInterface.graphLayer);
  performCull();

  pluginInterface.events.addListener('beforeRender', () => {
    // if viewport content changed, re-calculate culled elements
    performCull();
  });
  pluginInterface.events.addListener('nodeAdded', ({ node, id }) => {
    installCullHideProperty(node, (hide) =>
      toggleHideNode(pluginInterface, id, 'CULLED', hide),
    );
  });
  pluginInterface.events.addListener('edgeAdded', ({ edge, id }) => {
    installCullHideProperty(edge, (hide) =>
      toggleHideEdge(pluginInterface, id, 'CULLED', hide),
    );
  });
  pluginInterface.events.addListener('nodeBoundsChange', ({ node }) => {
    (node as any)[CULL_DIRTY_PROPERTY] = true;
  });
  pluginInterface.events.addListener(
    'nodePositionChangeInSequence',
    ({ node }) => {
      (node as any)[CULL_DIRTY_PROPERTY] = true;
    },
  );
  pluginInterface.events.addListener('edgeBoundsChange', ({ edge }) => {
    (edge as any)[CULL_DIRTY_PROPERTY] = true;
  });
  pluginInterface.events.addListener('beforeGenerateScreenshot', () => {
    uncull(pixiCull);
  });
};
